import { Button, GroupedGroupOption, Modal, SelectOptionType, SliderModal } from '@bp/ui-components';
import { FC, ReactNode, useCallback, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useLoadBasicData } from '../../../hooks/useLoadBasicData';
import type {
  AscImportReport,
  AscMappingKind,
  AscUnresolvedEntity,
  AscUnresolvedKind,
} from '../../../utils/timetable/timetableVersion';
import { AscImportKindSlide } from '../components/AscImportKindSlide';
import { AscImportOverviewSlide } from '../components/AscImportOverviewSlide';
import { useAscImportLabels } from '../hooks/useAscImportLabels';
import { ascGroupOptionsByEntry, buildAscGroupOptions } from '../utils/ascGroupOptions';
import { ASC_MAPPING_SKIP } from '../utils/ascMapping';
import styles from './AscImportReportModal.module.scss';

/**
 * EIN Collator für alle Sortierungen. `localeCompare(a, b, options)` erzeugt pro Aufruf implizit
 * einen neuen — bei hunderten Optionen × dutzenden Zeilen ist das der teuerste Einzelposten.
 */
const collator = new Intl.Collator(undefined, { numeric: true, sensitivity: 'base' });
const byLabel = (a: SelectOptionType, b: SelectOptionType) => collator.compare(String(a.label), String(b.label));

/** Arten, die der Anwender selbst zuordnen kann; LESSON/PERIOD ergeben sich daraus. */
const MAPPABLE_KINDS = new Set<AscUnresolvedKind>(['SUBJECT', 'TEACHER', 'CLASS', 'ROOM', 'GROUP']);

/** Reihenfolge der Folien: erst was den Import blockiert, dann die Feinheiten. */
const KIND_ORDER: AscUnresolvedKind[] = ['CLASS', 'SUBJECT', 'TEACHER', 'GROUP', 'ROOM', 'LESSON', 'PERIOD'];

type AscImportReportModalProps = {
  report: AscImportReport | null;
  /** Getroffene Zuordnungen, Schlüssel `<kind>:<ascId>`. `null` = überspringen. */
  mappings: Record<string, string | null>;
  onMappingChange: (entry: AscUnresolvedEntity, targetUuid: string | null | undefined) => void;
  onRecheck: () => void;
  /**
   * `preview` = Trockenlauf, es wurde noch nichts geschrieben; das Modal fragt dann nach
   * Bestätigung. `result` = der Import ist gelaufen, es gibt nur noch „Schließen".
   */
  variant: 'preview' | 'result';
  busy?: boolean;
  onConfirm?: () => void;
  onClose: () => void;
};

/**
 * Vorschau bzw. Ergebnis eines aSc-Imports.
 *
 * Die Vorschau ist ein Assistent: eine Folie Übersicht, danach je Art der nicht auflösbaren
 * Entitäten eine eigene. Arten ohne Einträge bekommen gar keine Folie — bei einer kompletten
 * Schule kommen sonst 50+ Zuordnungen auf einmal, und eine Liste über alle Arten hinweg ist weder
 * lesbar noch abzuarbeiten.
 *
 * Das Ergebnis ist dagegen ein reiner Bericht: nichts mehr zu entscheiden, deshalb ein normales
 * Modal mit allen Arten untereinander statt eines Assistenten ohne Ziel.
 */
export const AscImportReportModal: FC<AscImportReportModalProps> = ({
  report,
  mappings,
  onMappingChange,
  onRecheck,
  variant,
  busy = false,
  onConfirm,
  onClose,
}) => {
  const { t } = useTranslation();
  const isPreview = variant === 'preview';
  const labels = useAscImportLabels(isPreview);
  const [sliderIndex, setSliderIndex] = useState(0);
  const { subjectData, subjectContainerData, teacherData, roomsData, classesData, groupsData, divisionsData } =
    useLoadBasicData({ pause: false });

  const skipOption: SelectOptionType = useMemo(
    () => ({ value: ASC_MAPPING_SKIP, label: t('pinboard.actions.ascImportMapping.skip') }),
    [t],
  );

  // `GroupPicker` kennt kein Skip-Sentinel — deshalb reist „überspringen" als eigene Pseudo-Teilung
  // (Gruppe ohne Kopfzeile) mit. Referenzstabil, damit die Optionslisten es nicht bei jedem Render neu bauen.
  const skipGroup: GroupedGroupOption = useMemo(
    () => ({ label: '', options: [{ value: ASC_MAPPING_SKIP, label: t('pinboard.actions.ascImportMapping.skip') }] }),
    [t],
  );
  const skipGroupOnly = useMemo(() => [skipGroup], [skipGroup]);

  // Optionslisten EINMAL je Art bauen — alle Zeilen bekommen dieselbe Referenz.
  const optionsByKind = useMemo(() => {
    const subjects = [
      ...(subjectData?.subjects ?? []).map((entity) => ({ value: entity.uuid, label: entity.name })),
      ...(subjectContainerData?.subjectContainers ?? []).map((entity) => ({
        value: entity.uuid,
        label: `${entity.name} (Schiene)`,
      })),
    ].sort(byLabel);

    return {
      SUBJECT: [skipOption, ...subjects],
      TEACHER: [
        skipOption,
        ...(teacherData?.people ?? [])
          .map((entity) => ({ value: entity.uuid, label: entity.displayName ?? entity.lastName }))
          .sort(byLabel),
      ],
      ROOM: [
        skipOption,
        ...(roomsData?.rooms ?? []).map((entity) => ({ value: entity.uuid, label: entity.name })).sort(byLabel),
      ],
      CLASS: [
        skipOption,
        ...(classesData?.classes ?? []).map((entity) => ({ value: entity.uuid, label: entity.name })).sort(byLabel),
      ],
      // GROUP läuft über den `GroupPicker` mit verschachtelten Optionen (s. `groupOptionsByEntry`).
    } satisfies Record<Exclude<AscMappingKind, 'GROUP'>, SelectOptionType[]>;
  }, [subjectData, subjectContainerData, teacherData, roomsData, classesData, skipOption]);

  // Gruppen sind klassen- UND teilungsgebunden — Auswahllisten je Klasse und je Teilung, beide
  // referenzstabil (s. `ascGroupOptions`).
  const groupOptions = useMemo(
    () => buildAscGroupOptions(divisionsData?.divisions ?? [], groupsData?.groups ?? [], skipGroup),
    [groupsData, divisionsData, skipGroup],
  );

  const groupOptionsByEntry = useMemo(
    () => ascGroupOptionsByEntry(report?.unresolved ?? [], mappings, groupOptions, skipGroupOnly),
    [report, mappings, groupOptions, skipGroupOnly],
  );

  const optionsFor = (entry: AscUnresolvedEntity): SelectOptionType[] =>
    optionsByKind[entry.kind as Exclude<AscMappingKind, 'GROUP'>];

  /** Nur GROUP: die nach Teilung verschachtelten Optionen für den `GroupPicker`. */
  const groupOptionsFor = (entry: AscUnresolvedEntity): GroupedGroupOption[] =>
    groupOptionsByEntry.get(entry.ascId)?.options ?? skipGroupOnly;

  /** Auf welche Teilung die Auswahl einer Gruppen-Zeile begrenzt ist — sonst wirkt sie zu kurz. */
  const divisionNameFor = (entry: AscUnresolvedEntity): string | undefined =>
    entry.kind === 'GROUP' ? groupOptionsByEntry.get(entry.ascId)?.divisionName : undefined;

  // Gruppennamen wie „A"/„1" sind ohne Klasse nicht auseinanderzuhalten — die Klasse gehört an jede
  // Gruppen-Zeile, sonst steht eine reine Namensliste da, die niemand zuordnen kann.
  const classNameById = useMemo(
    () => new Map((classesData?.classes ?? []).map((entity) => [entity.uuid, entity.name])),
    [classesData],
  );

  const classNameFor = (entry: AscUnresolvedEntity): string | undefined =>
    entry.kind === 'GROUP' && entry.classUuid ? classNameById.get(entry.classUuid) : undefined;

  // Über Refs statt Abhängigkeiten: der Handler muss referenzstabil bleiben, sonst rendern bei
  // jeder Zuordnung alle Zeilen neu (sie sind genau deswegen memoisiert).
  const mappingsRef = useRef(mappings);
  mappingsRef.current = mappings;
  const entriesRef = useRef(report?.unresolved);
  entriesRef.current = report?.unresolved;

  /**
   * Wechselt eine Gruppen-Zeile die Teilung, verlieren die Geschwister-Zuordnungen derselben
   * ASC-Teilung ihre Gültigkeit: sie stehen dann nicht mehr zur Auswahl, blieben aber unsichtbar
   * gesetzt und würden vom Backend verworfen. Also mit zurücksetzen.
   */
  const handleMappingChange = useCallback(
    (entry: AscUnresolvedEntity, targetUuid: string | null | undefined) => {
      onMappingChange(entry, targetUuid);
      if (entry.kind !== 'GROUP' || !entry.ascDivisionId || !targetUuid) return;

      const divisionUuid = groupOptions.divisionOfGroup.get(targetUuid);
      for (const sibling of entriesRef.current ?? []) {
        if (sibling.kind !== 'GROUP' || sibling.ascId === entry.ascId) continue;
        if (sibling.ascDivisionId !== entry.ascDivisionId) continue;
        const chosen = mappingsRef.current[`GROUP:${sibling.ascId}`];
        if (chosen && groupOptions.divisionOfGroup.get(chosen) !== divisionUuid) {
          onMappingChange(sibling, undefined);
        }
      }
    },
    [onMappingChange, groupOptions],
  );

  if (!report) {
    return null;
  }

  // Arten ohne Einträge fallen hier raus — daraus entsteht weder eine Folie noch ein Abschnitt.
  const groups = KIND_ORDER.map((kind) => ({
    kind,
    entries: report.unresolved.filter((entry) => entry.kind === kind),
  })).filter((group) => group.entries.length > 0);

  const modalSettings = {
    width: 'm' as const,
    shouldCloseOnEsc: !busy,
    shouldCloseOnOverlayClick: !busy,
    parent: document.fullscreenElement ? document.getElementById('timetable-grid') : null,
  };

  const overview = <AscImportOverviewSlide key='overview' report={report} labels={labels} isPreview={isPreview} />;

  if (isPreview) {
    // Die Bilanz steht am Ende: Sie zeigt, was die getroffenen Zuordnungen bringen (der Wechsel auf
    // die letzte Folie rechnet sie neu), und steht damit direkt über „Jetzt importieren". Gibt es
    // nichts zuzuordnen, ist sie die einzige Folie — sonst bliebe der Dialog leer.
    const slides: ReactNode[] = [
      ...groups.map((group) =>
        MAPPABLE_KINDS.has(group.kind) ? (
          <AscImportKindSlide
            key={group.kind}
            kind={group.kind}
            entries={group.entries}
            labels={labels}
            mappable={true}
            optionsFor={optionsFor}
            groupOptionsFor={groupOptionsFor}
            classNameFor={classNameFor}
            divisionNameFor={divisionNameFor}
            mappings={mappings}
            onMappingChange={handleMappingChange}
            busy={busy}
          />
        ) : (
          // LESSON/PERIOD ergeben sich aus den anderen Arten — die Folie zeigt sie nur an, sonst
          // bliebe unsichtbar, was der Import überspringt.
          <AscImportKindSlide
            key={group.kind}
            kind={group.kind}
            entries={group.entries}
            labels={labels}
            mappable={false}
          />
        ),
      ),
      overview,
    ];

    return (
      <SliderModal
        isOpen={true}
        title={labels.title}
        slides={slides}
        slideClassName={styles.slide}
        modalSettings={modalSettings}
        closeText={t('common.cancel')}
        previousText={t('common.back')}
        nextText={t('common.next')}
        submitText={t('pinboard.actions.ascImportPreview.confirm')}
        startIndex={sliderIndex}
        onChange={(key) => {
          setSliderIndex(key);
          // Auf der Bilanz-Folie mit den bis dahin getroffenen Zuordnungen neu rechnen.
          if (key === slides.length - 1) {
            onRecheck();
          }
        }}
        // Bestätigen erst auf der letzten Folie — abgeleitet statt als eigener State, sonst bliebe
        // der Button aus, wenn es nur die Bilanz-Folie gibt (onChange feuert dann nie).
        submitDisabled={busy || report.cardsImported === 0 || sliderIndex < slides.length - 1}
        loading={busy}
        onSubmit={() => onConfirm?.()}
        onClose={onClose}
      />
    );
  }

  return (
    <Modal
      {...modalSettings}
      isOpen={true}
      onRequestClose={onClose}
      title={labels.title}
      hideFooter={false}
      footer={
        <Button hierarchy='tertiary' onClick={onClose}>
          {t('common.close')}
        </Button>
      }
    >
      {overview}

      {groups.length > 0 && (
        <div className={styles.groups}>
          {groups.map((group) => (
            <div key={group.kind}>
              <AscImportKindSlide
                kind={group.kind}
                entries={group.entries}
                labels={labels}
                mappable={false}
                showHint={false}
              />
            </div>
          ))}
        </div>
      )}
    </Modal>
  );
};
